/**
 * @fileoverview Singer Dashboard Page
 * @summary Displays singer's event history, songs added, and statistics
 * 
 * @package KaraoNow
 * @module apps/front/src/pages/singer/SingerDashboardPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../hooks/redux.js'
import PageHeader from '../../components/PageHeader.js'
import Spinner from '../../components/Spinner.js'
import { useTranslation } from 'react-i18next'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'
import { EventsTabSection } from './components/EventsTabSection.js'
import { SongsTabSection } from './components/SongsTabSection.js'

interface SingerEvent {
  id: string
  name: string
  organizerName: string
  date: string
  status: 'draft' | 'active' | 'completed' | 'cancelled'
  songsCount: number
}

interface SingerSong {
  id: string
  title: string
  artist: string
  eventName: string
  organizerName: string
  addedAt: string
  eventDate?: string
}

/**
 * @summary Singer Dashboard Page - Shows event history, songs, and stats
 * @returns {React.ReactElement} Singer Dashboard Page
 */
export default function SingerDashboardPage(): React.ReactElement {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user = useAppSelector((state: any) => state.auth.user)
  const hasInitialized = useRef(false)

  const [events, setEvents] = useState<SingerEvent[]>([])
  const [songs, setSongs] = useState<SingerSong[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [eventFilter, setEventFilter] = useState<'all' | 'active' | 'past' | 'inactive'>('all')
  const [activeTab, setActiveTab] = useState<'events' | 'songs'>('events')

  useEffect(() => {
    if (!user || hasInitialized.current) {
      return
    }

    if (!user) {
      navigate('/login')
      return
    }

    hasInitialized.current = true

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const eventsResponse = await apiRequest({ method: 'get', url: API_ENDPOINTS.USERS.EVENTS })
        const eventsData = eventsResponse.data?.events || []
        setEvents(eventsData)

        const historyResponse = await apiRequest({ method: 'get', url: API_ENDPOINTS.USERS.HISTORY })
        const historyData = historyResponse.data?.history || []
        setSongs(historyData)
      } catch (err: any) {
        setError(err?.message || t('common.errorFetching'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user, navigate, t])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    )
  }

  // Filter events
  const filteredEvents = events.filter((event) => {
    if (eventFilter === 'all') return true
    const eventDate = new Date(event.date)
    const now = new Date()
    
    if (eventFilter === 'active') {
      return event.status === 'active' || (eventDate > now && event.status !== 'cancelled')
    }
    if (eventFilter === 'past') {
      return eventDate < now && event.status !== 'cancelled'
    }
    if (eventFilter === 'inactive') {
      return event.status === 'cancelled' || event.status === 'draft'
    }
    return true
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'badge-success'
      case 'completed':
        return 'badge-secondary'
      case 'cancelled':
        return 'badge-error'
      case 'draft':
        return 'badge-warning'
      default:
        return 'badge-ghost'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="min-h-screen bg-base-50 dark:bg-base-900">
      <div className="page-container">
        <PageHeader title={t('singer.dashboard')} subtitle={t('singer.dashboardSubtitle')} />

        {error && (
          <div className="alert alert-error mb-4 text-sm">
            <span>{error}</span>
          </div>
        )}

        {/* Tabs Navigation as Buttons */}
        <div className="mb-6 flex flex-wrap gap-2 justify-evenly">
          <button
            type="button"
            onClick={() => setActiveTab('events')}
            className={`btn btn-sm ${activeTab === 'events' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('singer.myEvents')} ({filteredEvents.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('songs')}
            className={`btn btn-sm ${activeTab === 'songs' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('singer.mySongs')} ({songs.length})
          </button>
        </div>

        {/* Events Tab */}
        {activeTab === 'events' && (
          <EventsTabSection
            events={filteredEvents}
            eventFilter={eventFilter}
            onFilterChange={setEventFilter}
            getStatusColor={getStatusColor}
            formatDate={formatDate}
            onJoinEvent={(eventId) => navigate(`/singer/${eventId}`)}
          />
        )}

        {/* Songs Tab */}
        {activeTab === 'songs' && (
          <SongsTabSection
            songs={songs}
            formatDate={formatDate}
          />
        )}
      </div>
    </div>
  )
}
